import React, {
  FormEvent,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo
} from 'react';
import {
  Box,
  Flex,
  FormInput,
  IconButton,
  Text,
  styled
} from '@sparrowengg/twigs-react';
import { CloseIcon, ImageIcon } from '@sparrowengg/twigs-react-icons';
import { LocalDevAppFileUploadsProps } from '../types';
import { fileUploadTypes } from '../constants';
import FileUpload from 'commons/icons/file-upload';
import { truncateText } from 'commons/helper';

const FileUploads = ({
  getValues,
  setValue,
  watch
}: LocalDevAppFileUploadsProps) => {
  const iconRef = useRef<HTMLInputElement | null>(null);
  const prevIconRef = useRef<any>(null);
  const [previewImage, setPreviewImage] = useState<string | ArrayBuffer | null>(
    ''
  );
  const [iconUrl, setIconUrl] = useState<string | null>(null);
  const [fileName, setFileName] = useState({
    iconName: ''
  });
  // Stable image src state - only updates when icon actually changes
  const [stableImageSrc, setStableImageSrc] = useState<string | null>(null);
  const [stableFileName, setStableFileName] = useState<string>('');
  const { ICON } = fileUploadTypes;

  const handleFileInput = (e: FormEvent) => {
    const inputValue = e.target as HTMLInputElement;
    const file: FileList | null = inputValue?.files;
    if (file && file?.length) {
      const selectedFile = file[0];
      const fileId = `${selectedFile.name}-${selectedFile.size}-${selectedFile.lastModified}`;

      // Update refs immediately to prevent reloads
      prevIconRef.current = fileId;
      stableFileNameRef.current = selectedFile.name;
      setStableFileName(selectedFile.name);
      isIconLoadedRef.current = true;

      let reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Update ref first (source of truth)
        stableImageSrcRef.current = result;
        // Update stable state (only updates when icon changes)
        setStableImageSrc(result);
        setPreviewImage(result);
        setFileName((prev) => ({ ...prev, iconName: selectedFile.name }));
        setIconUrl(null);
      };
      reader.readAsDataURL(selectedFile);
      setValue('icon', selectedFile, { shouldDirty: true });
    }
  };

  const StyledImage = styled('img', {
    width: 200,
    height: 200,
    borderRadius: '10px',
    objectFit: 'cover'
  });

  // Store stable image source in ref to prevent reloads - this is the source of truth
  const stableImageSrcRef = useRef<string | null>(null);
  const stableFileNameRef = useRef<string>('');
  const isIconLoadedRef = useRef(false);

  // Process icon changes with stable comparison
  const processIcon = useCallback(
    (iconValue: any, forceUpdate: boolean = false) => {
      const currentIconData = iconValue?.data || iconValue;

      // Create a stable identifier for comparison
      let currentIconId: string | null = null;
      if (currentIconData instanceof File) {
        currentIconId = `${currentIconData.name}-${currentIconData.size}-${currentIconData.lastModified}`;
      } else if (typeof currentIconData === 'string') {
        currentIconId = currentIconData;
      } else if (!currentIconData) {
        currentIconId = null;
      }

      // Only update if icon actually changed (unless forced, like on initial load)
      if (
        !forceUpdate &&
        currentIconId === prevIconRef.current &&
        isIconLoadedRef.current
      ) {
        return;
      }

      prevIconRef.current = currentIconId;

      if (currentIconData) {
        if (
          typeof currentIconData === 'string' &&
          (currentIconData.startsWith('http') ||
            currentIconData.startsWith('https'))
        ) {
          // Only update if URL actually changed
          if (stableImageSrcRef.current !== currentIconData) {
            const splitString = currentIconData.split('/');
            const extractedFileName = splitString[splitString.length - 1];
            stableFileNameRef.current = extractedFileName;
            stableImageSrcRef.current = currentIconData;
            setStableImageSrc(currentIconData);
            setStableFileName(extractedFileName);
            setFileName((prev) => ({
              ...prev,
              iconName: extractedFileName
            }));
            setIconUrl(currentIconData);
            setPreviewImage(null);
            isIconLoadedRef.current = true;
          }
        } else if (currentIconData instanceof File) {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            stableImageSrcRef.current = result;
            stableFileNameRef.current = currentIconData.name;
            setStableImageSrc(result);
            setStableFileName(currentIconData.name);
            setPreviewImage(result);
            setFileName((prev) => ({
              ...prev,
              iconName: currentIconData.name
            }));
            setIconUrl(null);
            isIconLoadedRef.current = true;
          };
          reader.readAsDataURL(currentIconData);
        }
      } else {
        stableImageSrcRef.current = null;
        stableFileNameRef.current = '';
        setStableImageSrc(null);
        setStableFileName('');
        setFileName({ iconName: '' });
        setPreviewImage(null);
        setIconUrl(null);
        isIconLoadedRef.current = false;
      }
    },
    []
  );

  // Initialize icon - check on mount and periodically until loaded
  // This handles the case where form is populated asynchronously
  useEffect(() => {
    const checkAndLoadIcon = () => {
      if (isIconLoadedRef.current) return;

      const currentIcon = getValues('icon');

      if (currentIcon) {
        // Force update on initial load to ensure icon is displayed
        if (currentIcon?.data) {
          // Icon.data is the URL string
          processIcon(currentIcon, true);
        } else if (
          typeof currentIcon === 'string' &&
          (currentIcon.startsWith('http') || currentIcon.startsWith('https'))
        ) {
          // Handle case where icon is directly a URL string
          processIcon({ data: currentIcon }, true);
        } else if (currentIcon instanceof File) {
          // Handle case where icon is a File object
          processIcon(currentIcon, true);
        }
      }
    };

    // Check immediately
    checkAndLoadIcon();

    // If not loaded, check again after a short delay (handles async form population)
    if (!isIconLoadedRef.current) {
      const timeoutId1 = setTimeout(() => {
        checkAndLoadIcon();
      }, 100);

      // Check one more time after a longer delay
      const timeoutId2 = setTimeout(() => {
        checkAndLoadIcon();
        // If still no icon after all checks, mark as loaded to stop checking
        if (!isIconLoadedRef.current) {
          isIconLoadedRef.current = true;
        }
      }, 500);

      return () => {
        clearTimeout(timeoutId1);
        clearTimeout(timeoutId2);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount - icon updates are handled via handleFileInput

  // Use stable state values - these only update when icon actually changes
  // This prevents image reloads on re-renders
  // Priority: stableImageSrc > previewImage > iconUrl
  const imageSrcToDisplay =
    stableImageSrc ||
    (previewImage ? (previewImage as string) : '') ||
    iconUrl ||
    '';
  const fileNameToDisplay = stableFileName || fileName.iconName;
  const hasImage = !!imageSrcToDisplay;

  return (
    <Flex flexDirection="column" gap="$20" css={{ marginRight: '$20' }}>
      <Flex
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        onClick={() => iconRef?.current?.click()}
        css={{
          width: 200,
          height: 200,
          border: '2px dashed rgba(100, 116, 139, 0.15)',
          borderRadius: '10px',
          cursor: 'pointer'
        }}
      >
        <input
          type="file"
          accept=".png, .jpg, .jpeg"
          ref={iconRef}
          onChange={(e: FormEvent) => handleFileInput(e)}
          style={{ display: 'none' }}
        />
        {hasImage ? (
          <Box
            css={{
              transition: 'all .3s ease',
              position: 'relative',
              '&:hover': {
                '#show-btn': {
                  opacity: 1
                },
                img: {
                  opacity: 1
                }
              }
            }}
          >
            <StyledImage
              src={imageSrcToDisplay}
              alt={fileNameToDisplay}
              key={prevIconRef.current || 'icon'}
            />
          </Box>
        ) : (
          <Flex
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            css={{ padding: '$16 $12' }}
          >
            <ImageIcon color="#6A6A6A" size={48} />
            <Text
              size="md"
              css={{ marginTop: '$8', color: '$neutral800' }}
              weight="medium"
            >
              Upload App Icon
            </Text>
            <Text
              size="xs"
              css={{
                textAlign: 'center',
                textWrap: 'pretty',
                lineHeight: '$xs',
                color: '$neutral500'
              }}
            >
              Use JPG, PNG upto 5MB max. Recommended size is 400 x 400px
            </Text>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

// Memoize component to prevent re-renders when parent re-renders
export default React.memo(FileUploads);
